import React from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Auto-reload on ChunkLoadError (stale JS bundles after deployment)
    if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      console.log('  ChunkLoadError detected - reloading page to fetch fresh bundles...');
      // Give user a moment to see what happened, then reload
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      // Check if it's a ChunkLoadError
      const isChunkError = this.state.error?.name === 'ChunkLoadError' || 
                          this.state.error?.message?.includes('Loading chunk');
      
      return (
        <Box
          sx={{
            height: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(101, 80, 130,  1) 0%, rgba(4101, 80, 130,  1) 50%, rgba(16, 61, 62, 1) 100%)',
            color: 'white',
            p: 4,
          }}
        >
          <Alert severity={isChunkError ? "info" : "error"} sx={{ mb: 3, maxWidth: 600 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {isChunkError ? "Loading fresh content..." : "Something went wrong"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {isChunkError 
                ? "New version detected. Reloading to fetch latest updates..."
                : (this.state.error && this.state.error.toString())
              }
            </Typography>
            {!isChunkError && this.state.errorInfo && (
              <details style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                {this.state.errorInfo.componentStack}
              </details>
            )}
          </Alert>
          {!isChunkError && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
